import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useLens } from './LensProvider';

const CoherencePolygon = () => {
  const { capacityVector, gradientVector, pressure, axis_descriptions, support_affordances, hoveredEvent, coherenceMemory } = useLens();
  const [hoveredAxis, setHoveredAxis] = useState(null);
  const [time, setTime] = useState(0);

  useEffect(() => {
    let animationFrameId;
    const loop = (timestamp) => {
      setTime(timestamp / 1000); // Time in seconds
      animationFrameId = requestAnimationFrame(loop);
    };
    animationFrameId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animationFrameId);
  }, []);

  const size = 420;
  const center = size / 2;
  const radius = size * 0.38;
  const numAxes = capacityVector.length;

  const getPoint = useCallback((value, index) => {
    const angle = (index / numAxes) * 2 * Math.PI;
    const x = center + value * radius * Math.cos(angle);
    const y = center + value * radius * Math.sin(angle);
    return { x, y };
  }, [numAxes, center, radius]);

  const polygonPoints = useMemo(() => {
    const MAX_DISTORTION = 10;
    const distortion = pressure * MAX_DISTORTION;

    return capacityVector.map((axis, i) => {
      const angle = (i / numAxes) * 2 * Math.PI;
      let { x, y } = getPoint(axis.value, i);

      if (distortion > 0.1) {
        const noiseFrequency = 4;
        // Add a small random phase per-axis to make it less uniform
        const phase = i * Math.PI / 3;
        const noise = Math.sin(time * noiseFrequency + angle * 3 + phase) * distortion;
        const distortedRadius = radius * axis.value + noise;
        x = center + distortedRadius * Math.cos(angle);
        y = center + distortedRadius * Math.sin(angle);
      }

      return `${x},${y}`;
    }).join(' ');
  }, [capacityVector, getPoint, pressure, time, numAxes, center, radius]);

  const previewPolygonPoints = useMemo(() => {
    if (!hoveredEvent) return null;
    return capacityVector.map((axis, i) => {
      const boost = hoveredEvent.coherenceVector[axis.id] || 0;
      const newValue = Math.max(0, Math.min(1, axis.value + boost));
      const { x, y } = getPoint(newValue, i);
      return `${x},${y}`;
    }).join(' ');
  }, [hoveredEvent, capacityVector, getPoint]);

  const echoPolygonPoints = useMemo(() => {
    return capacityVector.map((axis, i) => {
      const value = coherenceMemory[axis.id] || 0;
      const { x, y } = getPoint(value, i);
      return `${x},${y}`;
    }).join(' ');
  }, [capacityVector, getPoint, coherenceMemory]);

  if (numAxes === 0) return null;

  const axisLines = capacityVector.map((_, i) => {
    const { x, y } = getPoint(1, i);
    return { x1: center, y1: center, x2: x, y2: y };
  });

  const gradientGlows = gradientVector.map((axis, i) => {
    if (axis.gradient <= 0) return null;
    const startPoint = getPoint(axis.value, i);
    const endPoint = getPoint(axis.value + axis.gradient, i);
    return (
      <line
        key={`gradient-${i}`}
        x1={startPoint.x}
        y1={startPoint.y}
        x2={endPoint.x}
        y2={endPoint.y}
        stroke="rgba(246, 173, 85, 0.4)"
        strokeWidth="12"
        strokeLinecap="round"
      />
    );
  });

  const axisLabels = capacityVector.map((axis, i) => {
    const { x, y } = getPoint(1.15, i);
    return (
        <text
            key={`label-${i}`}
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize="12"
            fill="var(--text-secondary)"
            onMouseEnter={() => setHoveredAxis(axis.id)}
            onMouseLeave={() => setHoveredAxis(null)}
        >
            {axis.label}
        </text>
    );
  });

  return (
    <div className="coherence-polygon">
        <svg width={size} height={size}>
            {/* Axis Lines */}
            {axisLines.map((line, i) => (
            <line key={`axis-${i}`} {...line} stroke="var(--axis-line)" />
            ))}

            {/* Coherence Echo */}
            <polygon points={echoPolygonPoints} fill="rgba(147, 130, 255, 0.1)" stroke="rgba(147, 130, 255, 0.25)" />

            {/* Polygon */}
            <polygon points={polygonPoints} fill="rgba(99, 179, 237, 0.25)" stroke="rgba(99, 179, 237, 0.8)" strokeWidth="2" />

            {/* Preview Polygon */}
            {previewPolygonPoints && (
              <polygon points={previewPolygonPoints} fill="rgba(72, 187, 120, 0.15)" stroke="rgba(72, 187, 120, 0.6)" strokeDasharray="6" />
            )}

            {/* Gradient Glows */}
            {gradientGlows}

            {/* Axis Labels */}
            {axisLabels}
        </svg>

        {hoveredAxis && (
            <div className="tooltip">
                <strong>{capacityVector.find(a => a.id === hoveredAxis).label}</strong>
                <p>{axis_descriptions[hoveredAxis]}</p>
                <ul>
                    {support_affordances[hoveredAxis].map(aff => <li key={aff}>{aff}</li>)}
                </ul>
            </div>
        )}
    </div>
  );
};

export default CoherencePolygon;
